import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, MapPin, DollarSign, Rocket, HeartHandshake, TrendingUp, Clock, Copy, Briefcase } from "lucide-react";
import { TermsAgree, TERMS_VERSION } from "@/components/TermsAgree";

const PERKS = [
  { Icon: DollarSign, title: "9% → 22.5% per spot", body: "Start at 9% commission and climb to 22.5% as a Director — roughly half the profit on a full card. Your rank is set by how many active cards you manage." },
  { Icon: TrendingUp, title: "Climb the ladder", body: "Associate (1–3 cards) 9% · Manager (4–7) 13.5% · Senior Manager (8–11) 18% · Director (12+) 22.5%. The more cards you run, the higher every commission." },
  { Icon: HeartHandshake, title: "Team overrides", body: "Help others win and earn on your team too: 15% on your direct recruits and 5% on theirs (keep 4+ active cards to qualify)." },
  { Icon: MapPin, title: "100% remote", body: "Work from anywhere in the U.S. Own your schedule and your territory. Not a guarantee of income — your effort drives your results." },
];

export default function Careers() {
  const { user } = useAuth();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apply, setApply] = useState({ open: false, role: null });
  const [form, setForm] = useState({ name: "", email: "", phone: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [agree, setAgree] = useState(false);
  const refCode = user?.referly_code;

  useEffect(() => {
    api.get("/careers/roles").then((r) => setRoles(r.data || [])).catch(() => setRoles([])).finally(() => setLoading(false));
  }, []);

  const openApply = (role) => { setForm({ name: user?.name || "", email: user?.email || "", phone: "", note: "" }); setAgree(false); setApply({ open: true, role }); };

  const submit = async () => {
    if (!form.name || !form.email) { toast.error("Please add your name and email"); return; }
    if (!agree) { toast.error("Please agree to the Terms of Service"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/careers/apply", {
        name: form.name, email: form.email, phone: form.phone, note: form.note,
        role_id: apply.role.id, affiliate_id: window.affiliateId || undefined, terms_version: TERMS_VERSION,
      });
      toast.success("You're in! Opening your interview…");
      setApply({ open: false, role: null });
      if (data.interview_url) window.open(data.interview_url, "_blank", "noopener");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit — please try again");
    } finally { setBusy(false); }
  };

  const shareLink = refCode ? `${window.location.origin}/careers?ref=${refCode}` : "";

  return (
    <div data-testid="careers-page" className="bg-sam-cream">
      {/* Hero */}
      <section className="relative overflow-hidden bg-sam-navy text-sam-cream">
        <div className="sam-flag-bar h-1 w-full" />
        <div className="mx-auto max-w-5xl px-6 py-16 sm:py-24">
          <div className="inline-flex items-center gap-2 rounded-full bg-sam-crimson/20 border border-sam-crimson/40 px-3 py-1 text-xs font-bold uppercase tracking-widest text-sam-amber mb-5" data-testid="careers-eyebrow">
            <Rocket className="h-3.5 w-3.5" /> Now hiring · Remote
          </div>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            Build a career <span className="text-sam-crimson">Saving America Money</span>
          </h1>
          <p className="mt-5 max-w-2xl text-base sm:text-lg text-sam-cream/85 leading-relaxed">
            Help local businesses get seen and help neighbors save — and earn <b className="text-sam-amber">9% to 22.5% per spot</b> as you grow to Director (about 50% of a full card's profit), plus team overrides on everyone you help succeed. Remote, uncapped, and genuinely the opportunity of a lifetime.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#open-roles"><Button size="lg" className="bg-sam-crimson hover:bg-sam-crimson/90 font-bold" data-testid="careers-hero-cta">See open roles</Button></a>
            <span className="inline-flex items-center gap-1.5 text-sm text-sam-cream/70 self-center"><Clock className="h-4 w-4" /> 10-minute AI interview — apply on your time</span>
          </div>
        </div>
      </section>

      {/* Perks */}
      <section className="mx-auto max-w-6xl px-6 -mt-10 sm:-mt-12 relative z-10">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {PERKS.map((p) => (
            <div key={p.title} className="rounded-2xl bg-card border border-border p-5 shadow-sm" data-testid={`perk-${p.title.split(" ")[0].toLowerCase()}`}>
              <div className="grid place-items-center h-10 w-10 rounded-xl bg-sam-navy/5 text-sam-crimson mb-3"><p.Icon className="h-5 w-5" /></div>
              <div className="font-display font-bold text-sam-navy">{p.title}</div>
              <p className="mt-1.5 text-sm text-sam-slate leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Roles */}
      <section id="open-roles" className="mx-auto max-w-4xl px-6 py-16 sm:py-20">
        <h2 className="font-display font-extrabold text-sam-navy text-2xl sm:text-3xl">Open roles</h2>
        <p className="text-sam-slate mt-1 text-base">Pick a role and complete a short AI interview. No scheduling, no waiting.</p>
        <p className="text-sam-navy mt-1 text-sm font-semibold" data-testid="careers-mail-rule">Cards mail at 10+ paid spots and $10,500+ in sales; commissions pay 15 days after mailing.</p>
        <div className="mt-6 space-y-4">
          {loading ? (
            <div className="py-12 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-sam-slate" /></div>
          ) : roles.length === 0 ? (
            <p className="text-sam-slate text-sm py-8">No open roles right now — check back soon.</p>
          ) : roles.map((role) => (
            <div key={role.id} className="rounded-2xl border border-border bg-card p-6 shadow-sm" data-testid={`role-${role.id}`}>
              <div className="flex items-start gap-3">
                <div className="grid place-items-center h-11 w-11 rounded-xl bg-sam-crimson/10 text-sam-crimson shrink-0"><Briefcase className="h-5 w-5" /></div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-display font-bold text-sam-navy text-lg leading-tight">{role.title}</h3>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs font-semibold text-sam-slate">
                    {role.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-sam-green" />{role.location}</span>}
                    {role.comp && <span className="inline-flex items-center gap-1"><DollarSign className="h-3.5 w-3.5 text-sam-amber" />{role.comp}</span>}
                  </div>
                  {role.blurb && <p className="mt-3 text-sm text-sam-slate leading-relaxed">{role.blurb}</p>}
                  <Button className="mt-4 bg-sam-navy hover:bg-sam-navy/90 font-bold" onClick={() => openApply(role)} data-testid={`apply-${role.id}`}>Apply & start interview</Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Affiliate share (for logged-in partners) */}
        {refCode && (
          <div className="mt-10 rounded-2xl border border-sam-amber/40 bg-amber-50 p-5" data-testid="careers-referral-card">
            <div className="font-display font-bold text-sam-navy">Refer a future teammate — and get credit</div>
            <p className="text-sm text-sam-slate mt-1">Share your personal careers link. Anyone who applies through it is tracked to you for referral rewards.</p>
            <div className="mt-3 flex gap-2">
              <input readOnly value={shareLink} onFocus={(e) => e.target.select()} className="flex-1 min-w-0 text-xs font-mono bg-white border border-border rounded px-2 py-2" data-testid="careers-referral-link" />
              <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(shareLink); toast.success("Link copied"); }} data-testid="careers-referral-copy"><Copy className="h-4 w-4 mr-1" />Copy</Button>
            </div>
          </div>
        )}
      </section>

      {/* Apply dialog */}
      <Dialog open={apply.open} onOpenChange={(o) => setApply((a) => ({ ...a, open: o }))}>
        <DialogContent data-testid="careers-apply-dialog">
          <DialogHeader>
            <DialogTitle>Apply — {apply.role?.title}</DialogTitle>
            <DialogDescription>Tell us who you are, then we'll open your short AI interview in a new tab.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label htmlFor="c-name">Full name</Label><Input id="c-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="careers-name" /></div>
            <div><Label htmlFor="c-email">Email</Label><Input id="c-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="careers-email" /></div>
            <div><Label htmlFor="c-phone">Phone (optional)</Label><Input id="c-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="careers-phone" /></div>
            <div><Label htmlFor="c-note">Anything we should know? (optional)</Label><Textarea id="c-note" rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="careers-note" /></div>
            <TermsAgree checked={agree} onChange={setAgree} testid="careers-terms" />
            <Button className="w-full bg-sam-crimson hover:bg-sam-crimson/90 font-bold" disabled={busy || !agree} onClick={submit} data-testid="careers-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}Submit & start interview
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
